import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import styled from 'styled-components';
import * as http from 'utils/http';
import { ButtonContainer, Half } from '@ontruck/fe-ui';
import ToolbarWithNoPortal from 'modules/carriersv2/toolbarWithNoPortal';
import PageContent from 'modules/emergencies/components/ui/page/pageContent';
import ToolbarContainer from 'modules/emergencies/components/ui/containers/toolbarContainer';
import ToolbarTitle from 'modules/emergencies/components/ui/toolbar/toolbarTitle';
import { CustomSticky } from 'modules/emergencies/components/ui/sticky/CustomSticky';
import { FormButton } from 'modules/emergencies/components/ui/buttons/FormButton';
import { SectionContainer } from 'modules/emergencies/components/ticketForm/components/SectionContainer';
import { SectionBody } from 'modules/emergencies/components/ticketForm/components/SectionBody';
import { SectionLine } from 'modules/emergencies/components/ticketForm/components/SectionLine';
import RedBackgroundText from 'modules/emergencies/components/ui/texts/RedBackgroundText';
import PremiumSection from 'modules/emergencies/components/ticketForm/components/PremiumSection';
import PremiumSectionContainer from 'modules/emergencies/components/ticketForm/components/PremiumSectionContainer';
import BasicLoader from 'modules/emergencies/components/ui/loaders/basicLoader';
import { StyledLoaderContainer } from 'modules/Common/Loader/styled';
import moment from 'moment';

const BAF_CURRENT_URL = '/api/pricing-console/baf-current/';
const BAF_UPDATE_URL = '/api/pricing-console/baf-update/';

const fetchCurrentBaf = () => http.get(BAF_CURRENT_URL);

const normalizeNumber = (value) => {
  if (value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const hasEffectiveChange = (initialValue, draftValue) => {
  const initialNumeric = normalizeNumber(initialValue);
  const draftNumeric = normalizeNumber(draftValue);

  if (initialNumeric === null || draftNumeric === null) {
    return false;
  }

  return initialNumeric !== draftNumeric;
};

const formatLastUpdate = (timestamp) => {
  if (!timestamp) {
    return 'No disponible';
  }

  const parsedDate = moment(timestamp);
  if (!parsedDate.isValid()) {
    return 'No disponible';
  }

  const formattedDate = parsedDate.locale('es').format('MMMM DD [de] YYYY | HH:mm');
  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
};

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FormTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const FormTitle = styled.div`
  color: var(--grey-900, #263340);
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const FormSubtitle = styled.div`
  color: var(--grey-700, #54708c);
  font-feature-settings: 'liga' off, 'clig' off;

  /* 16px/regular */
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
`;

const InputsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  width: 100%;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

const InputLabel = styled.label`
  color: var(--grey-700, #54708c);
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const NumericInput = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid var(--grey300);
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  &:disabled {
    background-color: var(--grey100);
  }
`;

const LastUpdateLine = styled(SectionLine)`
  display: flex;
  height: 44px;
  padding: 12px 8px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-bottom: 1px solid var(--grey-300, #ccd6e0);
  border-top: 1px solid var(--grey-300, #ccd6e0);
`;

const LastUpdateLabel = styled.span`
  color: var(--grey-700, #54708c);
  font-feature-settings: 'liga' off, 'clig' off;

  /* 14px/medium */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 114.286% */
`;

const LastUpdateValue = styled.span`
  color: var(--grey-700, #54708c);
  font-feature-settings: 'liga' off, 'clig' off;

  /* 14px/regular */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const InlineFeedback = styled.div`
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  background-color: ${({ type }) => (type === 'success' ? 'var(--success100)' : 'var(--error100)')};
  color: ${({ type }) => (type === 'success' ? 'var(--success700)' : 'var(--error700)')};
  border: 1px solid ${({ type }) => (type === 'success' ? 'var(--success700)' : 'var(--error700)')};
`;

export function PricingConsolePage() {
  const [draftCas, setDraftCas] = useState('');
  const [draftVlc, setDraftVlc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initializedFromServer, setInitializedFromServer] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const {
    data,
    error,
    mutate,
    isLoading: swrIsLoading,
  } = useSWR(BAF_CURRENT_URL, fetchCurrentBaf, {
    revalidateOnFocus: false,
  });

  const initialCas = data?.current_values?.CAS?.cost ?? '';
  const initialVlc = data?.current_values?.VLC?.cost ?? '';

  const isDraftCasValid = normalizeNumber(draftCas) !== null;
  const isDraftVlcValid = normalizeNumber(draftVlc) !== null;

  const hasCasChanged = hasEffectiveChange(initialCas, draftCas);
  const hasVlcChanged = hasEffectiveChange(initialVlc, draftVlc);
  const hasAnyChanged = hasCasChanged || hasVlcChanged;

  const isSaveDisabled = useMemo(() => {
    return swrIsLoading || isSubmitting || !isDraftCasValid || !isDraftVlcValid || !hasAnyChanged;
  }, [swrIsLoading, isSubmitting, isDraftCasValid, isDraftVlcValid, hasAnyChanged]);

  useEffect(() => {
    if (!data || initializedFromServer) {
      return;
    }

    setDraftCas(initialCas);
    setDraftVlc(initialVlc);
    setInitializedFromServer(true);
  }, [data, initialCas, initialVlc, initializedFromServer]);

  const handleCasChange = (event) => {
    setDraftCas(event.target.value);
    if (feedback) {
      setFeedback(null);
    }
  };

  const handleVlcChange = (event) => {
    setDraftVlc(event.target.value);
    if (feedback) {
      setFeedback(null);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    const changes = [];

    if (hasCasChanged) {
      changes.push({
        key: 'CAS',
        previous_value: initialCas,
        new_value: draftCas,
      });
    }

    if (hasVlcChanged) {
      changes.push({
        key: 'VLC',
        previous_value: initialVlc,
        new_value: draftVlc,
      });
    }

    if (!changes.length) {
      return;
    }

    setIsSubmitting(true);
    try {
      await http.post(BAF_UPDATE_URL, { changes });
      setFeedback({ type: 'success', message: 'BAF actualizado correctamente' });
      try {
        const latestData = await mutate();
        const nextCas = latestData?.current_values?.CAS?.cost ?? '';
        const nextVlc = latestData?.current_values?.VLC?.cost ?? '';
        setDraftCas(nextCas);
        setDraftVlc(nextVlc);
        setInitializedFromServer(true);
      } catch (_revalidateError) {
        // Keep user feedback tied only to update status; ignore revalidation failures.
      }
    } catch (_err) {
      setFeedback({
        type: 'error',
        message: 'No se pudo actualizar el BAF, recargue la página y vuelva a intentarlo.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (
    // true ||
    error &&
    !data
  ) {
    return (
      <Layout>
        <ToolbarWithNoPortal>
          <ToolbarContainer noPaddingX>
            <ToolbarTitle>BAF - Bunker Adjustment Factor</ToolbarTitle>
          </ToolbarContainer>
        </ToolbarWithNoPortal>
        <PageContent>
          <PremiumSectionContainer>
            <PremiumSection>
              <RedBackgroundText>Ha ocurrido un error al cargar el BAF actual</RedBackgroundText>
            </PremiumSection>
          </PremiumSectionContainer>
        </PageContent>
      </Layout>
    );
  }

  return (
    <Layout>
      <ToolbarWithNoPortal>
        <ToolbarContainer noPaddingX>
          <ToolbarTitle>BAF - Bunker Adjustment Factor</ToolbarTitle>
        </ToolbarContainer>
      </ToolbarWithNoPortal>
      <PageContent>
        <PremiumSectionContainer>
          <PremiumSection>
            <SectionContainer style={{ marginTop: '48px' }}>
              <FormTitleContainer>
                <FormTitle>BAF vigente</FormTitle>
                <FormSubtitle>
                  La actualización de estos valores se aplicará en el momento de guardar los
                  cambios, afectando al cálculo de precio de nuevas órdenes.
                </FormSubtitle>
              </FormTitleContainer>
              {swrIsLoading && !initializedFromServer ? (
                <StyledLoaderContainer style={{ marginTop: '100px' }}>
                  <BasicLoader size={10} />
                </StyledLoaderContainer>
              ) : (
                <SectionBody>
                  <SectionLine style={{ marginTop: '32px', marginBottom: '32px  ' }}>
                    <InputsContainer>
                      <InputContainer>
                        <InputLabel htmlFor="pricing-console-vlc">BAF Valencia</InputLabel>
                        <NumericInput
                          id="pricing-console-vlc"
                          type="number"
                          step="any"
                          inputMode="decimal"
                          value={draftVlc}
                          onChange={handleVlcChange}
                          disabled={isSubmitting}
                          width="100%"
                        />
                      </InputContainer>
                      <InputContainer>
                        <InputLabel htmlFor="pricing-console-cas">BAF Castellon</InputLabel>
                        <NumericInput
                          id="pricing-console-cas"
                          type="number"
                          step="any"
                          inputMode="decimal"
                          value={draftCas}
                          onChange={handleCasChange}
                          disabled={isSubmitting}
                          width="100%"
                        />
                      </InputContainer>
                    </InputsContainer>
                  </SectionLine>
                  <LastUpdateLine>
                    <LastUpdateLabel>Ultima actualizacion:</LastUpdateLabel>
                    <LastUpdateValue>{formatLastUpdate(data?.last_updated_at)}</LastUpdateValue>
                  </LastUpdateLine>
                  {!!feedback && (
                    <InlineFeedback type={feedback.type}>{feedback.message}</InlineFeedback>
                  )}
                </SectionBody>
              )}
            </SectionContainer>
          </PremiumSection>
        </PremiumSectionContainer>
      </PageContent>
      <CustomSticky>
        <ButtonContainer>
          <Half>
            <FormButton isDisabled={isSaveDisabled} onClick={handleSave}>
              {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
            </FormButton>
          </Half>
        </ButtonContainer>
      </CustomSticky>
    </Layout>
  );
}
